import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { intelligenceApi } from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { IntelligenceListPagination } from "@/components/intelligence/IntelligenceListPagination";
import { IntelligenceSeverityBadge } from "@/components/intelligence/IntelligenceSeverityBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatInr } from "@/lib/intelligenceFormat";

type Props = { basePath?: string };

export default function LeakageIntelligencePage({ basePath }: Props) {
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isInvestor = user?.role === "INVESTOR";
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
  const chainHospitals = user?.hospitals ?? [];
  const isChainScoped = isChainAdmin || investorHasChainScope;
  const [searchParams] = useSearchParams();
  const selectedCaseId = searchParams.get("case");
  const qc = useQueryClient();

  const defaultFinancialHospitalId = useMemo(
    () =>
      defaultFinancialReportHospitalId({
        isSuperAdmin,
        isChainScoped,
        activeHospitalId,
        fallbackHospitalId: chainHospitals[0]?.id ?? null,
      }),
    [isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
  );

  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [page, setPage] = useState(1);
  const [resolutionNote, setResolutionNote] = useState("");
  const pageSize = 25;

  useEffect(() => {
    if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
      setSelectedHospitalId(defaultFinancialHospitalId || "all");
    }
  }, [defaultFinancialHospitalId, selectedHospitalId]);

  const { data: superHospitals } = useQuery({
    queryKey: ["super-admin-hospitals-leakage"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const reportHospitalId = useMemo(
    () =>
      intelligenceReportHospitalId({
        showHospitalFilter,
        selectedHospitalId,
        isChainScoped,
      }),
    [showHospitalFilter, selectedHospitalId, isChainScoped]
  );

  useEffect(() => {
    setPage(1);
  }, [reportHospitalId, statusFilter, severityFilter]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["intelligence-leakage", reportHospitalId, statusFilter, severityFilter, page],
    queryFn: () =>
      intelligenceApi.getLeakage({
        hospitalId: reportHospitalId,
        status: statusFilter || undefined,
        severity: severityFilter || undefined,
        page,
        pageSize,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ["intelligence-leakage-detail", selectedCaseId],
    queryFn: () => intelligenceApi.getLeakageDetail(selectedCaseId!),
    enabled: !!selectedCaseId,
  });

  const acknowledgeMut = useMutation({
    mutationFn: (id: string) => intelligenceApi.acknowledgeLeakage(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage-detail"] });
    },
  });

  const resolveMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => intelligenceApi.resolveLeakage(id, note),
    onSuccess: () => {
      setResolutionNote("");
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage-detail"] });
    },
  });

  const dismissMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => intelligenceApi.dismissLeakage(id, note),
    onSuccess: () => {
      setResolutionNote("");
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-leakage-detail"] });
    },
  });

  const exportParams = {
    hospitalId: reportHospitalId,
    status: statusFilter || undefined,
    severity: severityFilter || undefined,
  };

  const detail = detailQuery.data;
  const navBase = basePath ?? (isSuperAdmin ? "/super-admin" : "/hospital-admin");

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <IntelligenceSubNav basePath={navBase} />
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Potential Revenue Leakage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Rule-based potential leakage cases — not confirmed financial loss until reviewed.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {showHospitalFilter && (
          <select
            className="border rounded-md px-3 py-1.5 text-sm"
            value={selectedHospitalId}
            onChange={(e) => setSelectedHospitalId(e.target.value)}
          >
            <option value="all">All hospitals</option>
            {(isSuperAdmin ? superHospitals : chainHospitals)?.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
        )}
        <select className="border rounded-md px-3 py-1.5 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="ACKNOWLEDGED">Acknowledged</option>
          <option value="UNDER_REVIEW">Under review</option>
          <option value="RESOLVED">Resolved</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
        <select className="border rounded-md px-3 py-1.5 text-sm" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
          <option value="">All severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="ATTENTION">Attention</option>
          <option value="OBSERVATION">Observation</option>
        </select>
        <IntelligenceServerExportButton
          report="leakage"
          params={exportParams}
          filename="revenue-leakage-export"
          disabled={isLoading}
        />
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading leakage cases…
        </div>
      )}

      {isError && (
        <Card className="border-destructive/50">
          <CardContent className="pt-4 text-sm text-destructive flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {(error as Error)?.message ?? "Failed to load leakage cases"}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Open</p><p className="text-2xl font-semibold">{data.summary.openCount}</p></CardContent></Card>
            <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Critical</p><p className="text-2xl font-semibold text-red-700">{data.summary.criticalCount}</p></CardContent></Card>
            <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Attention</p><p className="text-2xl font-semibold">{data.summary.attentionCount}</p></CardContent></Card>
            <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total potential</p><p className="text-2xl font-semibold">{formatInr(data.summary.totalPotentialAmount)}</p></CardContent></Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Cases</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[520px] overflow-auto">
                {data.cases.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No leakage cases match filters.</p>
                ) : (
                  data.cases.map((c) => (
                    <Link key={c.id} to={`?case=${c.id}`} className={`block border rounded-md p-3 text-sm hover:bg-muted/40 ${selectedCaseId === c.id ? "ring-2 ring-indigo-300" : ""}`}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <IntelligenceSeverityBadge severity={c.severity} />
                        <span className="font-medium">{c.title}</span>
                      </div>
                      <div className="text-muted-foreground mt-1 flex flex-wrap gap-3">
                        <span>{c.hospitalName}</span>
                        <span>{formatInr(c.amount ?? 0)}</span>
                        <span>{c.status}</span>
                      </div>
                    </Link>
                  ))
                )}
                <IntelligenceListPagination
                  page={data.pagination.page}
                  pageSize={data.pagination.pageSize}
                  total={data.pagination.total}
                  onPageChange={setPage}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Case detail</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {!detail ? (
                  <p className="text-muted-foreground">Select a case to view details.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2"><IntelligenceSeverityBadge severity={detail.severity} /><span className="font-semibold">{detail.title}</span></div>
                    {(detail.patientName || detail.patientPhone) && (
                      <div className="rounded-md border bg-slate-50 px-3 py-2 space-y-1">
                        {detail.patientName && (
                          <p><span className="text-muted-foreground">Patient:</span> {detail.patientName}</p>
                        )}
                        {detail.patientPhone && (
                          <p><span className="text-muted-foreground">Phone:</span> {detail.patientPhone}</p>
                        )}
                      </div>
                    )}
                    <p><span className="text-muted-foreground">Potential amount:</span> {formatInr(detail.amount ?? 0)}</p>
                    <p><span className="text-muted-foreground">Rule:</span> {detail.ruleName}</p>
                    <p><span className="text-muted-foreground">Status:</span> {detail.status}</p>
                    <p><span className="text-muted-foreground">Why flagged:</span> {detail.description}</p>
                    {detail.recommendedAction && <p><span className="text-muted-foreground">Recommended:</span> {detail.recommendedAction}</p>}
                    {detail.periodStart && <p><span className="text-muted-foreground">Period:</span> {detail.periodStart} — {detail.periodEnd}</p>}
                    <p><span className="text-muted-foreground">Source:</span> {detail.sourceModule} / {detail.sourceRecordId}</p>
                    {detail.resolutionNote && <p><span className="text-muted-foreground">Resolution note:</span> {detail.resolutionNote}</p>}
                    {detail.status === "OPEN" && (
                      <Button size="sm" variant="outline" onClick={() => acknowledgeMut.mutate(detail.id)} disabled={acknowledgeMut.isPending}>Acknowledge</Button>
                    )}
                    {(detail.status === "OPEN" || detail.status === "ACKNOWLEDGED" || detail.status === "UNDER_REVIEW") && (
                      <div className="space-y-2 pt-2 border-t">
                        <textarea className="w-full border rounded-md p-2 text-sm" rows={3} placeholder="Resolution note (required)" value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => resolveMut.mutate({ id: detail.id, note: resolutionNote })} disabled={!resolutionNote.trim() || resolveMut.isPending}>Resolve</Button>
                          <Button size="sm" variant="secondary" onClick={() => dismissMut.mutate({ id: detail.id, note: resolutionNote })} disabled={!resolutionNote.trim() || dismissMut.isPending}>Dismiss</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
